/*
 * Quick settings panel behaviour.
 *
 * The markup is a native <details>, so open/close, the button role, and
 * expanded state come from the browser. This file adds only what <details>
 * does not give you:
 *   - hand off between navigation and account/settings without overlapping
 *   - close on outside click and on Escape, returning focus to the trigger
 *   - roving arrow-key focus inside the Appearance radio group (APG pattern)
 *   - the Motion switch's click handler
 *
 * Theme and motion state themselves live in duo-theme.js / duo-motion.js;
 * this file never owns preference logic, it only wires the controls.
 */
(() => {
  'use strict';

  function setup(root) {
    const summary = root.querySelector('summary');
    const panel = root.querySelector('[data-duo-settings-panel]');
    if (!summary || !panel) return;

    summary.addEventListener('click', (event) => {
      if (root.open) return;
      const drawer = root.closest('header')?.querySelector('header-drawer');
      if (!drawer?.mainDetailsToggle?.open) return;
      drawer.closeMenuDrawer(event, summary, true);
      drawer.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'false');
    });

    function close(refocus) {
      if (!root.open) return;
      root.open = false;
      if (refocus) summary.focus();
    }

    /*
     * Mobile only: the panel goes position:fixed so it can hug the viewport's
     * right edge rather than the trigger's (the account glyph sits mid-row on
     * small screens, so a trigger-relative offset runs off the left side). A
     * fixed element cannot resolve `top: 100%` against its trigger, so measure
     * it here and hand the value to CSS.
     */
    function positionPanel() {
      if (!root.open) return;
      if (window.matchMedia && window.matchMedia('(min-width: 750px)').matches) {
        panel.style.removeProperty('--duo-settings-top');
        return;
      }
      const rect = summary.getBoundingClientRect();
      panel.style.setProperty('--duo-settings-top', Math.round(rect.bottom + 8) + 'px');
    }

    root.addEventListener('toggle', positionPanel);
    window.addEventListener('resize', positionPanel, { passive: true });
    window.addEventListener('scroll', positionPanel, { passive: true });

    document.addEventListener('click', (event) => {
      if (root.open && !root.contains(event.target)) close(false);
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopPropagation();
        close(true);
      }
    });

    // Appearance: roving focus, arrows move and select, matching the ARIA
    // radiogroup pattern people already know from native radios.
    const group = root.querySelector('[data-duo-theme-group]');
    if (group) {
      group.addEventListener('keydown', (event) => {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
        if (keys.indexOf(event.key) === -1) return;
        const items = Array.from(group.querySelectorAll('[data-duo-theme-choice]'));
        if (!items.length) return;
        const from = items.indexOf(document.activeElement);
        let next;
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = items.length - 1;
        else {
          const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
          next = ((from < 0 ? 0 : from) + step + items.length) % items.length;
        }
        event.preventDefault();
        items[next].focus();
        items[next].click();
      });
    }

    // Motion switch.
    const motionSwitch = root.querySelector('[data-duo-motion-switch]');
    if (motionSwitch) {
      motionSwitch.addEventListener('click', () => {
        if (!window.duoMotion) return;
        window.duoMotion.set(window.duoMotion.current === 'full' ? 'reduced' : 'full');
      });
      const sync = () => {
        if (!window.duoMotion) return;
        motionSwitch.setAttribute('aria-checked', window.duoMotion.current === 'full' ? 'true' : 'false');
      };
      document.addEventListener('duo:motion:change', sync);
      sync();
    }

    if (window.duoTheme && typeof window.duoTheme.sync === 'function') window.duoTheme.sync();
  }

  function init() {
    document.querySelectorAll('[data-duo-settings]').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor re-renders the header section on edit.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
